import { useState, useCallback, useEffect } from "react";

/*
  THE FOUNDRY — Image Fusion Research Lab for Yours Truly
  
  Setup (3 steps):
  1. Save this file as src/Foundry.jsx
  2. In App.jsx add these lines:
  
     import Foundry from "./Foundry";
     const isFoundryRoute = typeof window !== "undefined" && window.location.pathname.includes("foundry");
     // Then in the component, before the main return:
     if(isFoundryRoute) return(<Foundry />);
  
  3. Deploy: vercel --prod
  4. Visit: yourstruly.gift/foundry
  
  Uses:
  - /api/image-search (your existing SerpAPI route)
  - fal.ai direct calls (same key you already use)
  No new serverless routes needed.
*/

const IMG_SEARCH_API = "/api/image-search";

export default function Foundry() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [fusionPrompt, setFusionPrompt] = useState("");
  const [fusionResult, setFusionResult] = useState(null);
  const [fusionHistory, setFusionHistory] = useState([]);
  const [loading, setLoading] = useState({ search: false, fusion: false, generate: false });
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("search");
  const [genPrompt, setGenPrompt] = useState("");
  const [generatedImages, setGeneratedImages] = useState([]);
  const [customUrls, setCustomUrls] = useState([""]);
  const [falKey, setFalKey] = useState("");
  const [showKeyInput, setShowKeyInput] = useState(false);

  useEffect(() => {
    const k = localStorage.getItem("yt_fal_key");
    if (k) setFalKey(k);
    else setShowKeyInput(true);
  }, []);

  const saveKey = (k) => { setFalKey(k); localStorage.setItem("yt_fal_key", k); setShowKeyInput(false); };

  /* ─── IMAGE SEARCH (uses existing /api/image-search) ─── */
  const searchImages = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setLoading(p => ({ ...p, search: true }));
    setError("");
    try {
      const queries = searchQuery.split(",").map(q => q.trim()).filter(Boolean);
      const r = await fetch(IMG_SEARCH_API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ queries }) });
      const d = await r.json();
      if (!d.results) { setError("No results"); setLoading(p => ({ ...p, search: false })); return; }
      const imgs = [];
      Object.entries(d.results).forEach(([query, results]) => {
        results.forEach(img => {
          imgs.push({ url: img.url || img.thumbnail, thumbnail: img.thumbnail || img.url, width: img.width || 0, height: img.height || 0, title: img.title || query, query });
        });
      });
      imgs.sort((a, b) => (b.width * b.height) - (a.width * a.height));
      setSearchResults(imgs);
    } catch (e) { setError("Search failed: " + e.message); }
    setLoading(p => ({ ...p, search: false }));
  }, [searchQuery]);

  /* ─── GENERATE (FLUX Pro via fal.ai) ─── */
  const generateImages = useCallback(async () => {
    if (!genPrompt.trim() || !falKey) return;
    setLoading(p => ({ ...p, generate: true }));
    setError("");
    try {
      const res = await fetch("https://fal.run/fal-ai/flux-pro/v1.1", {
        method: "POST",
        headers: { Authorization: "Key " + falKey, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: genPrompt, image_size: "square_hd", num_images: 4, safety_tolerance: "2" }),
      });
      if (!res.ok) throw new Error("FLUX Pro returned " + res.status);
      const data = await res.json();
      setGeneratedImages((data.images || []).map((img, i) => ({
        url: img.url, thumbnail: img.url, title: "Generated " + (i + 1), query: genPrompt, width: img.width || 1024, height: img.height || 1024,
      })));
    } catch (e) { setError("Generation failed: " + e.message); }
    setLoading(p => ({ ...p, generate: false }));
  }, [genPrompt, falKey]);

  /* ─── FUSE (FLUX.2 Edit via fal.ai) ─── */
  const fuseImages = useCallback(async () => {
    if (selectedImages.length === 0 || !fusionPrompt.trim() || !falKey) return;
    setLoading(p => ({ ...p, fusion: true }));
    setError("");
    setFusionResult(null);
    try {
      const res = await fetch("https://fal.run/fal-ai/flux-2/edit", {
        method: "POST",
        headers: { Authorization: "Key " + falKey, "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: fusionPrompt, image_urls: selectedImages.map(s => s.url), image_size: "square_hd" }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "FLUX.2 returned " + res.status); }
      const data = await res.json();
      if (data.images?.[0]?.url) {
        setFusionResult(data.images[0].url);
        setFusionHistory(p => [{ url: data.images[0].url, prompt: fusionPrompt, time: new Date().toLocaleTimeString() }, ...p].slice(0, 20));
      } else setError("No result from FLUX.2");
    } catch (e) { setError("Fusion failed: " + e.message); }
    setLoading(p => ({ ...p, fusion: false }));
  }, [selectedImages, fusionPrompt, falKey]);

  /* ─── Selection helpers ─── */
  const toggleSelect = (img) => {
    setSelectedImages(prev => {
      const exists = prev.find(s => s.url === img.url);
      if (exists) return prev.filter(s => s.url !== img.url);
      if (prev.length >= 10) return prev;
      return [...prev, img];
    });
  };
  const isSelected = (url) => selectedImages.some(s => s.url === url);
  const getIndex = (url) => selectedImages.findIndex(s => s.url === url) + 1;

  const addCustomUrls = () => {
    const valid = customUrls.filter(u => u.trim().startsWith("http"));
    setSelectedImages(prev => [...prev, ...valid.map((url, i) => ({ url: url.trim(), thumbnail: url.trim(), title: "Custom " + (i + 1), query: "custom", width: 0, height: 0 }))].slice(0, 10));
    setCustomUrls([""]);
  };

  const prefillPrompt = () => {
    if (selectedImages.length === 1) setFusionPrompt("Transform this into a polished metallic sculpture on a round pedestal base, dramatic studio lighting, dark background, sculpture");
    else if (selectedImages.length === 2) setFusionPrompt("Combine image 1 and image 2 into a single cohesive sculpture. The artistic form of image 2 grows organically from the object in image 1, as if they are one piece. Polished metallic finish, round pedestal base, dramatic studio lighting, dark background, sculpture");
    else setFusionPrompt("Combine all " + selectedImages.length + " images into one sculpture. Blend naturally. Polished metallic finish, round pedestal, dramatic lighting, dark background, sculpture");
  };

  /* ─── Colors ─── */
  const C = { bg: "#08080a", panel: "#0e0e12", border: "#1a1a1e", borderLight: "#252528", gold: "#c9a96e", text: "#e0ddd6", textSoft: "#888", textMuted: "#555", error: "#c47070" };

  /* ─── Key setup screen ─── */
  if (showKeyInput) return (
    <div style={{ background: C.bg, height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Instrument Sans', system-ui" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=Instrument+Sans:wght@400;500;600&display=swap');`}</style>
      <div style={{ background: C.panel, border: "1px solid " + C.border, borderRadius: 16, padding: 40, width: 460, maxWidth: "90vw" }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: C.gold, marginBottom: 8 }}>The Foundry</h2>
        <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 24, lineHeight: 1.6 }}>Image fusion research lab. Paste your fal.ai key to start.</p>
        <label style={{ fontSize: 11, color: C.textSoft, letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>FAL.AI KEY</label>
        <input id="keyInput" type="password" placeholder="paste here..." style={{ width: "100%", background: C.bg, border: "1px solid " + C.borderLight, borderRadius: 8, padding: "12px 14px", color: C.text, fontSize: 14, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} autoFocus onKeyDown={e => { if (e.key === "Enter" && e.target.value.trim()) saveKey(e.target.value.trim()); }} />
        <p style={{ fontSize: 11, color: C.textMuted, marginTop: 6 }}>Get one at <a href="https://fal.ai/dashboard/keys" target="_blank" rel="noreferrer" style={{ color: C.gold, textDecoration: "none" }}>fal.ai/dashboard/keys</a></p>
        <button onClick={() => { const v = document.getElementById("keyInput")?.value?.trim(); if (v) saveKey(v); }} style={{ width: "100%", marginTop: 24, background: "linear-gradient(135deg, #c9a96e, #a8863a)", color: C.bg, border: "none", borderRadius: 10, padding: 16, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1, textTransform: "uppercase" }}>Enter The Foundry</button>
      </div>
    </div>
  );

  /* ─── Main UI ─── */
  return (
    <div style={{ background: C.bg, color: C.text, height: "100vh", fontFamily: "'Instrument Sans', system-ui", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=Instrument+Sans:wght@400;500;600&display=swap');*{box-sizing:border-box;}::-webkit-scrollbar{width:5px;}::-webkit-scrollbar-thumb{background:#333;border-radius:3px;}input:focus,textarea:focus{border-color:${C.gold}!important;outline:none;}@keyframes spin{to{transform:rotate(360deg);}}`}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", borderBottom: "1px solid " + C.border, flexShrink: 0 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, color: C.gold, margin: 0 }}>The Foundry</h1>
          <p style={{ fontSize: 10, color: C.textMuted, letterSpacing: 2, textTransform: "uppercase", margin: 0 }}>Search → Select → Fuse → See</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 26, fontWeight: 200, color: C.gold, fontFamily: "'Cormorant Garamond', serif" }}>{selectedImages.length}</span>
            <span style={{ fontSize: 11, color: C.textMuted }}>/10</span>
          </div>
          <button onClick={() => setShowKeyInput(true)} style={{ background: "transparent", border: "1px solid " + C.border, borderRadius: 6, padding: "5px 10px", color: C.textMuted, fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>Key</button>
          <a href="/" style={{ color: C.textMuted, fontSize: 11, textDecoration: "none" }}>← App</a>
        </div>
      </div>

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", flex: 1, overflow: "hidden" }}>

        {/* LEFT — Source */}
        <div style={{ borderRight: "1px solid " + C.border, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ display: "flex", borderBottom: "1px solid " + C.border, flexShrink: 0 }}>
            {[{ id: "search", l: "Search" }, { id: "generate", l: "Generate" }, { id: "url", l: "URLs" }].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ flex: 1, padding: 11, background: "transparent", border: "none", borderBottom: "2px solid " + (activeTab === t.id ? C.gold : "transparent"), color: activeTab === t.id ? C.gold : C.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>{t.l}</button>
            ))}
          </div>

          <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
            {activeTab === "search" && <>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && searchImages()} placeholder="mystery box COD, vine sculpture texture..." style={inputS(C)} />
                <button onClick={searchImages} disabled={loading.search} style={btnS(C, loading.search)}>{loading.search ? "..." : "Search"}</button>
              </div>
              <p style={{ fontSize: 10, color: C.textMuted, marginBottom: 14 }}>Comma-separate for multiple queries. Uses your existing SerpAPI route.</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
                {searchResults.map((img, i) => <ImageCard key={i} img={img} selected={isSelected(img.url)} index={getIndex(img.url)} onClick={() => toggleSelect(img)} C={C} />)}
              </div>
            </>}

            {activeTab === "generate" && <>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input value={genPrompt} onChange={e => setGenPrompt(e.target.value)} onKeyDown={e => e.key === "Enter" && generateImages()} placeholder="mystery box with vines growing from seams, sculpture" style={inputS(C)} />
                <button onClick={generateImages} disabled={loading.generate} style={btnS(C, loading.generate)}>{loading.generate ? "..." : "Generate 4"}</button>
              </div>
              <p style={{ fontSize: 10, color: C.textMuted, marginBottom: 14 }}>Generates 4 via FLUX Pro. Click to select as references.</p>
              {loading.generate && <Spinner C={C} msg="FLUX Pro generating 4 images..." />}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                {generatedImages.map((img, i) => <ImageCard key={i} img={img} selected={isSelected(img.url)} index={getIndex(img.url)} onClick={() => toggleSelect(img)} C={C} large />)}
              </div>
            </>}

            {activeTab === "url" && <>
              <p style={{ fontSize: 10, color: C.textMuted, marginBottom: 14 }}>Paste direct image URLs.</p>
              {customUrls.map((u, i) => <div key={i} style={{ marginBottom: 6 }}><input value={u} onChange={e => { const c = [...customUrls]; c[i] = e.target.value; setCustomUrls(c); }} placeholder="https://..." style={inputS(C)} /></div>)}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setCustomUrls(p => [...p, ""])} style={smallBtnS(C)}>+ Add</button>
                <button onClick={addCustomUrls} style={btnS(C)}>Add to Selection</button>
              </div>
            </>}
          </div>
        </div>

        {/* RIGHT — Fusion */}
        <div style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: 18, gap: 18 }}>
          {/* Selected */}
          <div>
            <h3 style={hdrS}>Selected References</h3>
            {selectedImages.length === 0 ? <div style={emptyS}>Click images on the left to select up to 10</div> : (
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {selectedImages.map((img, i) => (
                  <div key={i} style={{ position: "relative", width: 58, height: 58, borderRadius: 7, overflow: "hidden", border: "1px solid " + C.borderLight }}>
                    <div style={{ position: "absolute", top: 2, left: 2, background: C.gold, color: C.bg, width: 15, height: 15, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, zIndex: 2 }}>{i + 1}</div>
                    <img src={img.thumbnail || img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = img.url; }} />
                    <button onClick={() => setSelectedImages(p => p.filter((_, j) => j !== i))} style={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", borderRadius: "50%", width: 15, height: 15, fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
                  </div>
                ))}
                <button onClick={() => setSelectedImages([])} style={{ width: 58, height: 58, borderRadius: 7, border: "1px dashed " + C.border, background: "transparent", color: C.textMuted, fontSize: 8, cursor: "pointer", fontFamily: "inherit" }}>Clear</button>
              </div>
            )}
          </div>

          {/* Prompt + Fuse */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={hdrS}>Fusion Prompt</h3>
              <button onClick={prefillPrompt} style={smallBtnS(C)}>Auto-fill</button>
            </div>
            <textarea value={fusionPrompt} onChange={e => setFusionPrompt(e.target.value)} rows={4} placeholder={"How should the images combine?\ne.g. 'The vines from image 2 growing through the seams of the box in image 1, sculpture'"} style={{ width: "100%", background: C.panel, border: "1px solid " + C.borderLight, borderRadius: 8, padding: "11px 13px", color: C.text, fontSize: 12, fontFamily: "inherit", resize: "vertical", lineHeight: 1.5, outline: "none" }} />
            <button onClick={fuseImages} disabled={loading.fusion || selectedImages.length === 0 || !fusionPrompt.trim()} style={{ width: "100%", marginTop: 8, background: "linear-gradient(135deg, #c9a96e, #a8863a)", color: C.bg, border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Cormorant Garamond', serif", letterSpacing: 1, textTransform: "uppercase", opacity: (loading.fusion || selectedImages.length === 0 || !fusionPrompt.trim()) ? 0.3 : 1 }}>{loading.fusion ? "Fusing..." : "Fuse " + selectedImages.length + " Image" + (selectedImages.length !== 1 ? "s" : "")}</button>
          </div>

          {/* Result */}
          <div>
            <h3 style={hdrS}>Result</h3>
            {loading.fusion && <Spinner C={C} msg="FLUX.2 combining references..." />}
            {fusionResult && !loading.fusion && (
              <div style={{ borderRadius: 10, overflow: "hidden", border: "1px solid " + C.borderLight }}>
                <img src={fusionResult} alt="Fused" style={{ width: "100%", display: "block" }} />
                <div style={{ display: "flex", gap: 6, padding: 8 }}>
                  <a href={fusionResult} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: "center", padding: 7, border: "1px solid " + C.border, borderRadius: 6, color: C.gold, textDecoration: "none", fontSize: 11 }}>Full Size ↗</a>
                  <button onClick={() => toggleSelect({ url: fusionResult, thumbnail: fusionResult, title: "Fused", query: "fusion" })} style={{ flex: 1, ...smallBtnS(C), textAlign: "center" }}>Re-use as Ref</button>
                </div>
              </div>
            )}
            {!fusionResult && !loading.fusion && <div style={emptyS}>Select images + write a prompt → fuse</div>}
          </div>

          {/* History */}
          {fusionHistory.length > 1 && <div>
            <h3 style={hdrS}>History</h3>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {fusionHistory.slice(1).map((h, i) => (
                <div key={i} onClick={() => setFusionResult(h.url)} style={{ width: 54, height: 54, borderRadius: 7, overflow: "hidden", cursor: "pointer", border: "1px solid " + C.border, opacity: 0.65 }}>
                  <img src={h.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              ))}
            </div>
          </div>}

          {error && <div style={{ background: "rgba(180,60,60,0.1)", border: "1px solid rgba(180,60,60,0.2)", borderRadius: 8, padding: "9px 13px", color: C.error, fontSize: 11 }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

/* ─── Shared ─── */
const hdrS = { fontSize: 10, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", color: "#666", margin: "0 0 10px 0" };
const emptyS = { padding: "28px 14px", textAlign: "center", color: "#444", fontSize: 11, border: "1px dashed #222", borderRadius: 10 };
const inputS = (C) => ({ flex: 1, background: C.panel, border: "1px solid " + C.borderLight, borderRadius: 8, padding: "10px 13px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none", width: "100%" });
const btnS = (C, dis) => ({ background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: dis ? 0.5 : 1 });
const smallBtnS = (C) => ({ background: C.panel, border: "1px solid " + C.border, borderRadius: 6, padding: "5px 10px", color: C.textSoft, fontSize: 10, cursor: "pointer", fontFamily: "inherit" });

function Spinner({ C, msg }) {
  return <div style={{ textAlign: "center", padding: 40 }}><div style={{ width: 26, height: 26, border: "2px solid " + C.border, borderTopColor: C.gold, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" }} /><p style={{ color: C.textMuted, fontSize: 11, marginTop: 10 }}>{msg}</p></div>;
}

function ImageCard({ img, selected, index, onClick, C, large }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <div onClick={onClick} style={{ position: "relative", borderRadius: 8, overflow: "hidden", cursor: "pointer", border: "2px solid " + (selected ? C.gold : "transparent"), background: C.panel, boxShadow: selected ? "0 0 10px rgba(201,169,110,0.15)" : "none", transition: "all 0.2s" }}>
      {selected && <div style={{ position: "absolute", top: 4, left: 4, background: C.gold, color: C.bg, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, zIndex: 2 }}>{index}</div>}
      <img src={img.thumbnail || img.url} alt={img.title} style={{ width: "100%", height: large ? 180 : 105, objectFit: "cover", display: "block" }} onError={() => setBroken(true)} />
      <div style={{ padding: "4px 7px" }}>
        <div style={{ fontSize: 9, color: C.textMuted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{img.title?.slice(0, 42)}</div>
      </div>
    </div>
  );
}
